/**
 * 站内信通知
 * @author ritaxxu
 */

import { Pagination } from './common'

/** 消息级别 */
export enum InnerMsgLevel {
  /** 普通 */
  Normal = 1,
  /** 重要 */
  Important = 2,
}

/** 消息已读状态 */
export enum InnerMsgReadStatus {
  /** 未读 */
  Unread = 0,
  /** 已读 */
  Read = 1,
}

/** 发送方式 */
export enum InnerMsgSendType {
  /** 立即 */
  Immediate = 1,
  /** 单次定时 */
  Scheduled = 2,
  /** cron 周期 */
  Cron = 3,
}

/** 发送来源 */
export enum InnerMsgSourceType {
  /** 手动/定时 */
  Manual = 1,
  /** 自动规则 */
  AutoRule = 2,
}

/** 模板状态 */
export enum InnerMsgTemplateStatus {
  /** 草稿 */
  Draft = 0,
  /** 待触发 */
  Pending = 1,
  /** 已撤销 */
  Cancelled = 3,
  /** cron 启用中 */
  CronActive = 5,
  /** cron 已暂停 */
  CronPaused = 6,
  /** 已完成 */
  Completed = 7,
  /** 系统模板 */
  System = 8,
}

/** 消息列表项（我的消息） */
export interface IInnerMsgItem {
  /** recipient 表主键（调 MarkInnerMsgRead 时传这个） */
  id: number,
  /** 消息实例 ID */
  msgId: number,
  /** 消息标题 */
  title: string,
  /** 消息级别 1=普通 2=重要 */
  level: InnerMsgLevel,
  /** 阅读状态 0=未读 1=已读 */
  readStatus: InnerMsgReadStatus,
  /** 发送时间 */
  sentAt: string,
  /** 阅读时间 */
  readTime: string,
  /** 发送源 1=手动/定时 2=自动规则 */
  sourceType: InnerMsgSourceType,
  /** 自动规则名（sourceType=2 时显示） */
  sourceRuleName: string,
  /** 消息标签列表（后端下发，如 ["线索通知"]）；无标签为空数组 */
  tags: string[],
  /** 渲染后正文 HTML */
  content: string,
  /** 发送人 RTX */
  creator: string,
  /** 附件列表 */
  attachments?: IInnerMsgAttachment[],
}

/** 未读计数 */
export interface IInnerMsgUnreadCount {
  /** 全部未读数 */
  unreadCount: number,
  /** 重要未读数（level=2 且 readStatus=0） */
  importantUnreadCount: number,
  /** 重要未读的 recipientId 列表；len === importantUnreadCount；无排序保证；空时为 [] */
  importantUnreadIds: number[],
}

/** 我的消息列表请求参数 */
export interface IGetInnerMsgListReq {
  /** 0=未读 1=已读；不传不筛选 */
  readStatus?: InnerMsgReadStatus,
  /** 1=普通 2=重要；不传不筛选 */
  level?: InnerMsgLevel,
  page: number,
  pageSize: number,
}

/** 标记已读请求参数 */
export interface IMarkInnerMsgReadReq {
  /** 接收人记录 ID 列表（GetInnerMsgList 返回的 list[].id）；非空进入「按 ID」模式 */
  recipientIds?: number[],
  /** 按级别批量模式：仅允许 1（普通消息）；recipientIds 非空时忽略 */
  level?: InnerMsgLevel,
}

/** 附件对象 */
export interface IInnerMsgAttachment {
  /** 附件原始文件名 */
  name: string,
  /** COS 对象 key */
  cosKey: string,
  /** 文件大小（字节） */
  size: number,
  /** MIME 类型 */
  contentType: string,
}

/** 接收人配置 */
export interface IInnerMsgReceiverConfig {
  /** 公司 ID 列表 */
  companyIds?: number[],
  /** 群组 ID 列表 */
  groupIds?: number[],
  /** 座席 RTX 列表 */
  salesNames?: string[],
}

/** 发送通知请求参数 */
export interface ISendInnerMsgReq {
  /** 标题 */
  title: string,
  /** 正文 HTML */
  content: string,
  /** 附件 */
  attachments?: IInnerMsgAttachment[],
  /** 消息级别 1=普通 2=重要 */
  level: InnerMsgLevel,
  /** 接收人配置 */
  receiverConfig: IInnerMsgReceiverConfig,
  /** 发送方式 1=立即 2=单次定时 3=cron */
  sendType: InnerMsgSendType,
  /** 定时发送时间（sendType=2 必填） */
  scheduledAt?: string,
  /** cron 表达式（sendType=3 必填） */
  cronExpr?: string,
}

/** 再次发送请求参数 */
export interface IResendInnerMsgReq {
  /** 待再次发送的模板 ID（来自 GetSendRecordList list[].templateId） */
  templateId: number,
}

/** 发送记录视角 */
export enum SendRecordViewMode {
  /** 管理员视角（看所有人） */
  Admin = 1,
  /** 本人视角（仅看本人） */
  Self = 2,
}

/** 发送记录列表请求参数 */
export interface IGetSendRecordListReq {
  /** 0=不限 1=手动/定时 2=自动规则 */
  sourceType?: InnerMsgSourceType,
  /** 创建时间起 */
  createFrom?: string,
  /** 创建时间止 */
  createTo?: string,
  /** 发送人精确筛选（仅 hasView 用户有效） */
  creatorEqual?: string,
  /** 视角：1=管理员（看所有人，仅 hasView 用户有效） 2=本人（仅看本人） */
  viewMode?: SendRecordViewMode,
  page: number,
  pageSize: number,
}

/** 发送记录列表项 */
export interface ISendRecordItem {
  templateId: number,
  title: string,
  sourceType: InnerMsgSourceType,
  creator: string,
  sourceRuleId?: number,
  sourceRuleName: string,
  /** 消息标签列表（后端下发）；无标签为空数组 */
  tags: string[],
  sendType?: InnerMsgSendType,
  status: InnerMsgTemplateStatus,
  createTime: string,
  lastExecuteAt: string,
  totalRecipient: number,
  readCount: number,
  readRate: number,
}

/** 发送记录详情请求参数 */
export interface IGetSendRecordDetailReq {
  templateId: number,
  page: number,
  pageSize: number,
}

/** 发送记录详情中的触发实例项 */
export interface ISendRecordExecuteItem {
  msgId: number,
  executeRound: number,
  sentAt: string,
  totalRecipient: number,
  readCount: number,
  content: string,
  salesName: string,
  /** 附件列表 */
  attachments?: IInnerMsgAttachment[],
}

/** 发送记录详情响应 */
export interface IGetSendRecordDetailRsp {
  templateId: number,
  title: string,
  sourceType: InnerMsgSourceType,
  sourceRuleId?: number,
  sourceRuleName: string,
  total: number,
  list: ISendRecordExecuteItem[],
}

/** 单次执行接收人列表请求 */
export interface IGetSendRecordExecuteDetailReq {
  msgId: number,
  /** 已读状态筛选 0=未读 1=已读 */
  readStatus?: InnerMsgReadStatus,
  page: number,
  pageSize: number,
}

/** 单次执行接收人项 */
export interface ISendRecordRecipientItem {
  recipientId: number,
  salesName: string,
  companyId: number,
  groupId: number,
  readStatus: InnerMsgReadStatus,
  readTime: string,
}

/** 导出单次执行接收人列表请求（异步任务，不带分页） */
export interface IExportSendRecordExecuteDetailReq {
  msgId: number,
}

/** 组织架构树节点层级 */
export enum OrgTreeNodeLevel {
  /** 公司 */
  Company = 1,
  /** 群组 */
  Group = 2,
  /** 座席 */
  Sales = 3,
}

/** 组织架构树节点 */
export interface IOrgTreeNode {
  /** 节点 ID：公司=companyId 群组=groupId 座席=0 */
  id: number,
  /** 节点名称 */
  name: string,
  /** 层级：1=公司 2=群组 3=座席 */
  level: OrgTreeNodeLevel,
  /** 父公司 ID */
  parentCompanyId: number,
  /** 成员数量 */
  memberCount: number,
  /** 是否叶子节点 */
  isLeaf: boolean,
  /** 子节点列表 */
  children: IOrgTreeNode[],
  /** 座席 RTX 列表（仅叶子节点） */
  salesNames: string[],
}

/** 获取组织架构树响应 */
export interface IGetOrgTreeRsp {
  list: IOrgTreeNode[],
}
